/**
 * src/lib/capture/vcard.js — TICKET-926. A tiny, PURE vCard reader.
 *
 * A contact card (.vcf) dropped in the chat is parsed ON THE DEVICE — no upload,
 * no model, no paid call — and becomes directory contacts through the existing
 * create_vessel_contact call (see vcardCapture.js).
 *
 * Reads vCard 3.0 and 4.0 (2.1 cards parse too, minus QUOTED-PRINTABLE): FN, N,
 * EMAIL, TEL, ORG, TITLE. Handles folded lines (CRLF + space/tab), value escapes
 * (\, \; \n \\), property groups (item1.EMAIL), quoted parameters, and vCard 4
 * `tel:` / `mailto:` URI values. Everything else on the card is ignored.
 *
 * Business-card PHOTOS are deliberately NOT handled here: reading a photo needs a
 * vision call, which is a metered API and must go through the paid-API guardrails
 * (root CLAUDE.md). Deferred — a photo of a card still files as a photo.
 */

const VCARD_MIME = ['text/vcard', 'text/x-vcard', 'text/directory'];

/** A .vcf / .vcard file, or one the OS labelled with a vCard MIME type. */
export function isVcardFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase().split(';')[0].trim();
  return name.endsWith('.vcf') || name.endsWith('.vcard') || VCARD_MIME.includes(type);
}

/** RFC 6350 §3.2 unfolding: a line break followed by ONE space or tab is removed. */
function unfold(text) {
  return String(text).replace(/\r\n?/g, '\n').replace(/\n[ \t]/g, '');
}

/** Split on a separator that is not backslash-escaped. */
function splitUnescaped(value, sep) {
  const out = [];
  let cur = '';
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === '\\' && i + 1 < value.length) { cur += ch + value[i + 1]; i += 1; continue; }
    if (ch === sep) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function unescapeValue(v) {
  return String(v).replace(/\\([\\,;nN])/g, (_, c) => (c === 'n' || c === 'N' ? '\n' : c)).trim();
}

/** `[group.]NAME[;params]:value` -> { name, value }. The ':' inside a quoted param does not split. */
function parseLine(line) {
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') quoted = !quoted;
    else if (ch === ':' && !quoted) {
      const head = line.slice(0, i);
      const name = head.split(';')[0].split('.').pop().trim().toUpperCase();
      return { name, value: line.slice(i + 1) };
    }
  }
  return null;
}

const stripUri = (v, scheme) => (v.toLowerCase().startsWith(`${scheme}:`) ? v.slice(scheme.length + 1) : v);

function emptyCard() {
  return { full_name: '', first_name: '', last_name: '', emails: [], phones: [], organization: '', title: '' };
}

function finish(card, n) {
  if (n) {
    card.last_name = unescapeValue(n[0] || '');
    card.first_name = unescapeValue(n[1] || '');
  }
  if (!card.first_name && !card.last_name && card.full_name) {
    const [first, ...rest] = card.full_name.split(/\s+/);
    card.first_name = first;
    card.last_name = rest.join(' ');
  }
  return card;
}

/**
 * Every card in a vCard text. PURE.
 * @param {string} text
 * @returns {Array<{full_name:string, first_name:string, last_name:string, emails:string[], phones:string[], organization:string, title:string}>}
 */
export function parseVCards(text) {
  if (!text || typeof text !== 'string') return [];
  const cards = [];
  let card = null;
  let n = null;
  for (const raw of unfold(text).split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const upper = line.toUpperCase();
    if (upper === 'BEGIN:VCARD') { card = emptyCard(); n = null; continue; }
    if (upper === 'END:VCARD') {
      if (card) cards.push(finish(card, n));
      card = null;
      continue;
    }
    if (!card) continue;
    const prop = parseLine(line);
    if (!prop) continue;
    const { name, value } = prop;
    if (name === 'FN') card.full_name = unescapeValue(value);
    else if (name === 'N') n = splitUnescaped(value, ';');
    else if (name === 'EMAIL') {
      const v = stripUri(unescapeValue(value), 'mailto');
      if (v) card.emails.push(v);
    } else if (name === 'TEL') {
      const v = stripUri(unescapeValue(value), 'tel');
      if (v) card.phones.push(v);
    } else if (name === 'ORG') card.organization = unescapeValue(splitUnescaped(value, ';')[0] || '');
    else if (name === 'TITLE') card.title = unescapeValue(value);
  }
  return cards;
}

/**
 * A parsed card -> create_vessel_contact fields. Only what the card holds: no key
 * for an empty field, first email/phone only. Returns null for a card without a
 * person's name — a company-only card is not named after the company. PURE.
 */
export function vcardToContactPayload(card) {
  if (!card) return null;
  const first = card.first_name || card.last_name;
  if (!first) return null;
  const out = { first_name: first };
  if (card.first_name && card.last_name) out.last_name = card.last_name;
  if (card.emails?.[0]) out.email = card.emails[0];
  if (card.phones?.[0]) out.phone = card.phones[0];
  if (card.title) out.position = card.title;
  if (card.organization) out.company_name = card.organization;
  return out;
}
